// Phase 6 — per-project safety-module toggles. Defaults are derived from the
// project type on the backend; PR can override per project here.
import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Building2, Layers, Flame, ClipboardCheck } from "lucide-react";
import { api } from "@/lib/api";

const MODULES = [
    { key: "bsc", label: "Building Safety Case", icon: Building2 },
    { key: "golden_thread", label: "Golden Thread", icon: Layers },
    { key: "fire", label: "Fire Safety", icon: Flame },
    { key: "compliance", label: "Compliance", icon: ClipboardCheck },
];

export function ProjectModuleToggles({ project, canEdit, onChanged }) {
    const [flags, setFlags] = useState(project.module_flags || {});
    const [saving, setSaving] = useState(null);

    const toggle = async (key) => {
        if (!canEdit || saving) return;
        const next = { ...flags, [key]: !flags[key] };
        setFlags(next);
        setSaving(key);
        try {
            await api.patch(`/projects/${project.id}`, { module_flags: next });
            toast.success(`${MODULES.find((m) => m.key === key).label} ${next[key] ? "enabled" : "disabled"}`);
            onChanged?.();
        } catch {
            setFlags(flags); // revert
            toast.error("Could not update module");
        } finally {
            setSaving(null);
        }
    };

    return (
        <div data-testid="project-module-toggles" className="bg-white border rounded-[13px] shadow-brand-sm p-5" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center gap-2 mb-1">
                <ShieldCheck className="w-4 h-4" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                <h3 className="text-[14px] font-semibold text-[#1F1F1F]">Safety modules</h3>
            </div>
            <p className="text-[11.5px] text-[#9A968F] mb-4">
                Defaults follow the project type; toggle to show or hide these modules for this project (and its client portal).
            </p>
            <div className="grid sm:grid-cols-2 gap-2.5">
                {MODULES.map((m) => {
                    const on = !!flags[m.key];
                    return (
                        <button key={m.key} data-testid={`module-toggle-${m.key}`} onClick={() => toggle(m.key)}
                            disabled={!canEdit || saving === m.key}
                            className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-[10px] border text-left transition-colors ${on ? "bg-[#FBEFE5]" : "bg-[#FAF8F5]"} ${canEdit ? "hover:border-[var(--brand-accent,#C75A1B)]" : "cursor-default"}`}
                            style={{ borderColor: on ? "var(--brand-accent,#C75A1B)" : "#E5E0DA" }}>
                            <m.icon className="w-4 h-4 flex-shrink-0" style={{ color: on ? "var(--brand-accent,#C75A1B)" : "#9A968F" }} />
                            <span className={`text-[12.5px] font-semibold flex-1 ${on ? "text-[#1F1F1F]" : "text-[#6F6A64]"}`}>{m.label}</span>
                            <span className={`w-9 h-5 rounded-full relative transition-colors ${on ? "bg-[var(--brand-accent,#C75A1B)]" : "bg-[#D9D3CA]"}`}>
                                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${on ? "left-4" : "left-0.5"}`} />
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
